import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import './theme.css';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { API } from './constants';
import { logError } from './lib/log';
import { saveLastKit } from './lib/kitLogic';
import { useCart } from './hooks/useCart';
import { useCheckout } from './hooks/useCheckout';
import { useSharedKit } from './hooks/useSharedKit';

import Header from './components/Header';
import Hero3D from './components/Hero3D';
import CartModal from './components/CartModal';
import Footer from './components/Footer';
import Loader from './components/Loader';
import AmbientBackground from './components/AmbientBackground';
import PageTransition from './components/PageTransition';
import IframeNotice from './components/IframeNotice';
import KitMixer from './components/home/KitMixer';
import HowItWorks from './components/home/HowItWorks';
import ShareKitModal from './components/ShareKitModal';
import TrackOrderModal from './components/TrackOrderModal';
import AdminApp from './admin/AdminApp';

import BeautyKitPage from './pages/BeautyKitPage';
import ChiSiamo from './pages/ChiSiamo';
import Contatti from './pages/Contatti';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import CookiesPolicy from './pages/CookiesPolicy';

const STATIC_PAGES = { chisiamo: ChiSiamo, contatti: Contatti, privacy: PrivacyPolicy, terms: TermsConditions, cookies: CookiesPolicy };

function App() {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState('hero');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showTrackModal, setShowTrackModal] = useState(false);

  const goHome = useCallback(() => setActiveSection('hero'), []);
  const { cart, setCart, addItems, removeFromCart, clearCart, cartCount, cartTotal } = useCart();
  const { shareKit, mixerKit, openShareModal, closeShareModal, checkShareParam, loadSharedKit } = useSharedKit({ t, onNavigateHome: goHome });
  const { paymentLoading, setPaymentLoading, initiateCartCheckout, checkReturnFromStripe } = useCheckout({ cart, clearCart, onPaid: openShareModal, t });

  const openCartToast = (message) => toast.success(message, { action: { label: t('cart.title'), onClick: () => setShowCartModal(true) } });

  const goToMixer = () => {
    setActiveSection('hero');
    setTimeout(() => document.getElementById('kit-mixer')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [activeSection]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/products`);
        setProducts(data);
        return data;
      } catch (error) {
        logError('Error fetching products:', error);
        setProducts([]);
        return [];
      } finally {
        setLoading(false);
      }
    };
    const initializeApp = async () => {
      try {
        checkReturnFromStripe();
        checkShareParam();
        const list = await fetchProducts();
        await loadSharedKit(list);
      } catch (error) {
        logError('App initialization error:', error);
        setLoading(false);
      }
    };
    initializeApp();
    const timeoutId = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleAddKit = (items, meta) => {
    if (items.length !== 5) { toast.error(t('home.mix.error')); return; }
    saveLastKit(items, meta);
    addItems(items);
    openCartToast(t('home.mix.added'));
  };

  const handleBuyKit = async (kit) => {
    const kitProducts = (kit.products || []).map((id) => products.find((p) => p.id === id)).filter(Boolean);
    if (kitProducts.length === 0) { toast.error(t('beautykit.buy_error')); return; }
    addItems(kitProducts, (p) => (kit.quantities && kit.quantities[p.id]) || 1);
    try {
      await axios.post(`${API}/beautykit/${kit.id}/purchase`);
    } catch (error) {
      logError('Could not increment kit purchase count:', error);
    }
    openCartToast(t('beautykit.buy_added'));
  };

  if (loading) return <Loader label={t('loading')} />;

  const renderSection = () => {
    if (activeSection === 'beautykit') return <BeautyKitPage onCreate={goToMixer} products={products} onBuyKit={handleBuyKit} />;
    const StaticPage = STATIC_PAGES[activeSection];
    if (StaticPage) return <StaticPage />;
    return (
      <>
        <Hero3D onMix={goToMixer} onCommunity={() => setActiveSection('beautykit')} />
        <KitMixer products={products} onAddKit={handleAddKit} initialKit={mixerKit} />
        <HowItWorks />
      </>
    );
  };

  return (
    <>
      <AmbientBackground />
      <div className="App text-white min-h-screen" data-testid="app-root">
        <Header activeSection={activeSection} setActiveSection={setActiveSection} cartCount={cartCount} setShowCartModal={setShowCartModal} />

        <PageTransition id={activeSection}>
          {renderSection()}
        </PageTransition>

        <Footer setActiveSection={setActiveSection} onTrackOrder={() => setShowTrackModal(true)} />

        <CartModal
          isOpen={showCartModal}
          onClose={() => setShowCartModal(false)}
          cart={cart}
          getCartTotal={() => cartTotal}
          getCartCount={() => cartCount}
          removeFromCart={removeFromCart}
          initiateCartCheckout={initiateCartCheckout}
          paymentLoading={paymentLoading}
          setPaymentLoading={setPaymentLoading}
          setCart={setCart}
        />

        {shareKit && <ShareKitModal kit={shareKit} onClose={closeShareModal} />}
        {showTrackModal && <TrackOrderModal onClose={() => setShowTrackModal(false)} />}
      </div>
    </>
  );
}

const AppWithLanguage = () => {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) return <AdminApp />;
  if (typeof window !== 'undefined' && window.self !== window.top) {
    try {
      window.top.location.href = window.location.href;
    } catch (error) {
      logError('Cannot break out of iframe:', error);
    }
    return <IframeNotice />;
  }

  return (
    <LanguageProvider>
      <App />
      <Toaster theme="dark" position="bottom-center" richColors closeButton toastOptions={{ style: { fontFamily: 'Comfortaa, sans-serif' } }} />
    </LanguageProvider>
  );
};

export default AppWithLanguage;
